import axios from "axios";
//Limitless imports
import { log as pilog } from "@limitless/common/utils";
import { uploadAttachment } from "@limitless/common/apis/attachments/AttachmentAPI";
import { createPlatformURL, handleSecureAjaxError, createMutationHeaders } from "../utils/SecurePlatformAPIUtils";
import { findMessageResources } from "./MessageResourceActions";
import { calculateMessageState } from "../utils/message-state-engine/MessageStateEngine";
// rferance
import { getGraphQLClient } from "../clients/GraphQLClient";
import { FIND_MESSAGE, GET_MESSAGE_CONFIG, GET_CUSTOMER_SECURE_DATA, FIND_MESSAGE_AIREVIEW } from "./queries/MessageQueries";
import { getAddDialogueQuery, CLAIM_MESSAGE, UNCLAIM_MESSAGE, CLAIM_TIMEOUT_INC, getAddAttachmentMutation } from "./queries/MessageMutations";
import { POLICY_SENSITIVE_DATA_REQUEST_ACCESS, POLICY_SENSITIVE_DATA_VIEW_ACCESS } from "../utils/CommonPolicyConstants";
import { doesUserHavePolicy } from "../utils/SecureUserValidation";
const ADD_DIALOGUE_END_STATES = ["Message not found in user's inbox, cannot proceed.", "GraphQL error: User does not have a claim for message, cannot proceed."];

/**
 * Helper method for validating if a messageId exists
 *
 * @param {string} messageId
 * @param {function} callback
 */
export const validateMessageId = (messageId, callback) => {
  const log = pilog.getLogger("MessageActions validateMessageId");
  log.debug(`Validating ${messageId}`);
  axios.get(createPlatformURL(`messages/${messageId}`), createMutationHeaders()).then(response => {
    log.debug("Validate Message ID response ", response);
    callback(response.data);
  }).catch(error => {
    handleSecureAjaxError(error, "MessageActions validateMessageId");
    callback(null);
  });
};
export const appendMessageConfig = (message, profile, callback) => {
  var _message$inbox;
  const graphQLClient = getGraphQLClient();
  if ((_message$inbox = message.inbox) !== null && _message$inbox !== void 0 && _message$inbox.claimed && doesUserHavePolicy(POLICY_SENSITIVE_DATA_REQUEST_ACCESS, profile)) {
    graphQLClient.query({
      query: GET_MESSAGE_CONFIG,
      variables: {
        messageId: message.messageId
      }
    }).then(response => {
      message.messageConfig = response.data.getMessageConfig;
      callback(message);
    }).catch(error => {
      handleSecureAjaxError(error, "MessageActions getMessageConfig");
      message.messageConfig = null;
      callback(message);
    });
  } else {
    callback(message);
  }
};
export const getCustomerData = (message, profile, callback) => {
  const graphQLClient = getGraphQLClient();
  if (doesUserHavePolicy(POLICY_SENSITIVE_DATA_VIEW_ACCESS, profile)) {
    graphQLClient.query({
      query: GET_CUSTOMER_SECURE_DATA,
      variables: {
        messageId: message.messageId
      },
      fetchPolicy: "no-cache"
    }).then(response => {
      callback({
        hasError: false,
        data: response.data.getCustomerSecureData
      });
    }).catch(error => {
      handleSecureAjaxError(error, "MessageActions getCustomerSecureData");
      callback({
        hasError: true
      });
    });
  } else {
    callback(message);
  }
};
export const fetchMessageAIReview = (messageId, callback) => {
  const graphQLClient = getGraphQLClient();
  graphQLClient.query({
    query: FIND_MESSAGE_AIREVIEW,
    variables: {
      messageId
    },
    fetchPolicy: "no-cache"
  }).then(response => {
    callback({
      hasError: false,
      aiReview: response.data.getPassiveAIResponse.aiReview,
      aiRespond: response.data.getPassiveAIResponse.aiRespond
    });
  }).catch(error => {
    handleSecureAjaxError(error, "MessageActions getPassiveAIResponse");
    callback({
      hasError: true,
      error
    });
  });
};
export const fetchExpertMessage = (profile, messageInbox, currentMessageResources, stateOverride, options, callback) => {
  const graphQLClient = getGraphQLClient();
  graphQLClient.query({
    query: FIND_MESSAGE,
    variables: {
      messageId: messageInbox.messageId
    },
    fetchPolicy: "no-cache"
  }).then(response => {
    const processCallback = message => processFindMessageResult(profile, message, currentMessageResources, stateOverride, options, callback);
    appendMessageConfig(response.data.findMessage, profile, message => processCallback(message));
  }).catch(error => {
    handleSecureAjaxError(error, "MessageActions fetchExpertMessage");
    callback({
      hasError: true,
      error
    });
  });
};
export const processFindMessageResult = (profile, message, currentMessageResources, stateOverride, options, callback) => {
  const log = pilog.getLogger("MessageActions postMessageHandler");
  if (message.expertAuthors) {
    try {
      message.expertAuthors = JSON.parse(message.expertAuthors);
    } catch (error) {
      log.info(`Failed to parse expertAuthors for ${message.messageId}`);
    }
  }
  if (message) {
    findMessageResources(profile, message, currentMessageResources, options, (message, messageResources, messageLabels) => {
      //calculate the state of the newly fetched message
      const messageWithState = calculateMessageState(profile, message, messageResources, stateOverride);
      callback(messageWithState, messageResources, messageLabels);
    });
  } else {
    log.error(`Could not find message ${message.messageId}`);
    callback({
      hasError: true
    });
  }
};
export const claimExpertMessage = (profile, message, messageResources, callback) => {
  const log = pilog.getLogger("MessageActions claimExpertMessage");
  const graphQLClient = getGraphQLClient();
  graphQLClient.mutate({
    mutation: CLAIM_MESSAGE,
    variables: {
      messageId: message.messageId
    }
  }).then(response => {
    const messageClaimResponse = response.data.claimMessage;
    if (messageClaimResponse && messageClaimResponse.claimed) {
      const claimedMessage = {
        ...message,
        claimedBy: messageClaimResponse.userId
      };
      if (claimedMessage && claimedMessage.inbox) {
        claimedMessage.inbox.claimed = true;
        claimedMessage.inbox.claimedAt = messageClaimResponse.claimedAt;
      }
      callback(claimedMessage);
    } else {
      log.error(`Could not claim message ${message.messageId}`);
      callback({
        hasError: true
      });
    }
  }).catch(error => {
    handleSecureAjaxError(error, "MessageActions claimExpertMessage");
    callback({
      hasError: true,
      error
    });
  });
};
export const addExpertDialogue = (message, payload, callback) => {
  const graphQLClient = getGraphQLClient();
  // Destructure and conditionally include AI variables
  const {
    isAIDraftSelected,
    feedback,
    lastCustomerDialogueId,
    ...restPayload
  } = payload;
  const aiPayload = isAIDraftSelected ? {
    isAIDraftSelected,
    feedback,
    lastCustomerDialogueId
  } : {};
  graphQLClient.mutate({
    mutation: getAddDialogueQuery(payload),
    variables: {
      messageId: message.messageId,
      ...restPayload,
      ...aiPayload
    }
  }).then(response => {
    const messageResponse = response.data.addDialogue;
    const lastDialogue = messageResponse.dialogues[messageResponse.dialogues.length - 1];
    callback(lastDialogue, messageResponse.custConfirmationState);
  }).catch(error => {
    handleSecureAjaxError(error, "MessageActions addExpertDialogue");
    const hasUserLostClaim = ADD_DIALOGUE_END_STATES.includes(error.message);
    callback({
      hasError: true,
      hasUserLostClaim,
      errorMessage: error.message
    });
  });
};
export const addExpertAttachment = (file, caption = "", profile, message, messageResources, uploadAttachmentOverride, callback) => {
  const graphQLClient = getGraphQLClient();
  const variables = {
    messageId: message.messageId,
    contentType: file.type,
    fileName: file.name,
    fileSize: file.size
  };
  if (caption) {
    variables.body = caption;
  }
  graphQLClient.mutate({
    mutation: getAddAttachmentMutation(variables, "addAttachmentDialogue"),
    variables
  }).then(response => {
    const url = response.data.addAttachmentDialogue.url;
    if (uploadAttachmentOverride) {
      uploadAttachmentOverride(url, callback, () => fetchMessagePostAttachmentUpload(profile, message, messageResources, callback));
    } else {
      uploadAttachment(url, file, result => {
        if (result.hasError) {
          callback(result);
        } else {
          fetchMessagePostAttachmentUpload(profile, message, messageResources, callback);
        }
      });
    }
  }).catch(error => {
    handleSecureAjaxError(error, "MessageActions addExpertAttachment");
    callback({
      hasError: true,
      error
    });
  });
};
const fetchMessagePostAttachmentUpload = (profile, message, messageResources, callback) => {
  fetchExpertMessage(profile, message.inbox, messageResources, message.conversationState.state, {}, newMessage => {
    callback(newMessage);
  });
};
export const unclaimExpertMessage = (message, callback) => {
  const log = pilog.getLogger("MessageActions unclaimExpertMessage");
  const graphQLClient = getGraphQLClient();
  graphQLClient.mutate({
    mutation: UNCLAIM_MESSAGE,
    variables: {
      messageId: message.messageId
    }
  }).then(response => {
    const unClaimedMessageInbox = response.data.unclaimMessage;
    if (unClaimedMessageInbox && !unClaimedMessageInbox.claimed) {
      callback({
        hasError: false
      });
    } else {
      log.warn(`No payload received for ${message.messageId} must have been swallowed by the platform`);
      callback({
        hasError: true
      });
    }
  }).catch(error => {
    handleSecureAjaxError(error, "MessageActions unclaimExpertMessage");
    callback({
      hasError: true,
      error
    });
  });
};
export const increaseMessageClaimTimeOut = ({
  messageId
}, callback) => {
  const log = pilog.getLogger("MessageActions increaseMessageClaimTimeOut");
  const graphQLClient = getGraphQLClient();
  graphQLClient.mutate({
    mutation: CLAIM_TIMEOUT_INC,
    variables: {
      messageId,
      typing: true
    }
  }).then(response => {
    const respondingMessageInbox = response.data.responding;
    if (respondingMessageInbox !== null && respondingMessageInbox !== void 0 && respondingMessageInbox.totalIncrement) {
      respondingMessageInbox.hasError = false;
      callback(respondingMessageInbox);
    } else {
      log.warn(`No payload received for ${messageId} must have been swallowed by the platform`);
      callback({
        hasError: true
      });
    }
  }).catch(error => {
    handleSecureAjaxError(error, "MessageActions increaseMessageClaimTimeOut");
    callback({
      hasError: true,
      error
    });
  });
};